import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Point = { x: number; y: number };

/**
 * A small floating menu anchored at a long-press point (see `useLongPress`),
 * rendered in a portal so it escapes the chat's `overflow` clipping. It sits
 * just above the finger (flipping below when there isn't room) and closes on an
 * outside tap, a scroll, a resize, or Escape. Desktop keeps its hover
 * affordances and never mounts this — it's the touch counterpart.
 */
export function LongPressMenu({
  point,
  onClose,
  children,
}: {
  point: Point;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Ignore the trailing tap of the press that opened the menu, so lifting the
  // finger doesn't immediately dismiss it.
  const [armed, setArmed] = useState(false);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const margin = 8;
    let left = point.x - rect.width / 2;
    let top = point.y - rect.height - 12;
    left = Math.max(margin, Math.min(left, window.innerWidth - rect.width - margin));
    if (top < margin) {
      // No room above the finger — drop below it instead.
      top = point.y + 16;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - rect.height - margin));
    setPos({ left, top });
  }, [point.x, point.y]);

  useEffect(() => {
    const armTimer = setTimeout(() => setArmed(true), 350);
    const close = () => onClose();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  const dismiss = () => {
    if (armed) {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50" onClick={dismiss} onTouchStart={dismiss}>
      <div
        ref={cardRef}
        className="fixed flex w-max max-w-[calc(100dvw-1rem)] items-center overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-zinc-900/10 dark:bg-zinc-900 dark:ring-white/10"
        style={{
          left: pos?.left ?? point.x,
          top: pos?.top ?? point.y,
          visibility: pos ? 'visible' : 'hidden',
        }}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
